import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft, Play, Pause, Plus, AlertTriangle, Square, Loader2,
  CheckCircle2, FlaskConical, Package,
} from 'lucide-react'
import toast from 'react-hot-toast'
import axios from 'axios'
import { useAuth } from '@/lib/auth'

const apiPub = axios.create({ baseURL: import.meta.env.VITE_API_URL || '/api', timeout: 30000 })

type Etapa = {
  id: number; codigo: string; nome: string; ordem: number; status: string
  quantidade_ok: string; quantidade_refugo: string; peso_ok: string
  tempo_setup_seg: number; tempo_pausado_seg: number
  iniciada_em: string | null; pausada_em: string | null
  requer_parametros: boolean; parametros_padrao: any[]
  tempo_padrao_min: number
}

type CompItem = {
  materia_prima_id: number; codigo: string; nome: string; unidade: string
  qtd_por_un: number; perda_pct: number
  qtd_total_op: number; custo_unitario: number; custo_total: number
}

type OPData = {
  op_id: number; op_numero: number; status: string
  produto_codigo: string | null; produto_descricao: string
  produto_horas_cura: number; produto_exige_laudo: boolean
  variante: string; quantidade: string; quantidade_produzida: string
  quantidade_refugo: string
  peso_planejado: string; peso_produzido: string
  fim_previsto: string | null; iniciada_em: string | null
  maquina_codigo: string | null; maquina_nome: string | null
  capacidade_hora_kg: number
  ficha_id: number | null; ficha_versao: number | null; ficha_revisao: string | null
  tempo_producao_min: number
  etapas: Etapa[]
  composicao: CompItem[]
  custo_previsto: number; custo_mp: number
  lote_codigo: string | null; lote_status: string | null
  lote_cura_fim_previsto: string | null
  cliente_nome: string | null; pedido_numero: number | null
}

type Motivo = { id: number; codigo: string; nome: string; categoria: string; cor?: string; reciclavel?: boolean }


export default function MobileOPApontarPage() {
  const { token } = useParams<{ token: string }>()
  const nav = useNavigate()
  const { user } = useAuth()
  const [op, setOp] = useState<OPData | null>(null)
  const [loading, setLoading] = useState(true)
  const [modal, setModal] = useState<null | 'parcial' | 'pausar' | 'refugo' | 'fim'>(null)
  const [showFicha, setShowFicha] = useState(false)

  async function load() {
    try {
      const r = await apiPub.get(`/m/op/${token}`)
      setOp(r.data)
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Erro')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [token])

  const etapaAtual = op?.etapas.find(e => e.status === 'em_execucao')
                  || op?.etapas.find(e => e.status === 'pendente')

  async function chamar(tipo: string, payload: any = {}) {
    if (!etapaAtual) return
    try {
      await apiPub.post(`/m/op/${token}/apontar`, {
        etapa_op_id: etapaAtual.id,
        operador_id: user?.id,
        tipo,
        ...payload,
      })
      toast.success('Registrado')
      load()
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Erro')
    }
  }

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-ink-50">
    <Loader2 className="h-12 w-12 animate-spin text-violet-600" />
  </div>
  if (!op) return null

  return (
    <div className="min-h-screen bg-ink-50 pb-24">
      <header className="bg-gradient-to-br from-ink-900 to-ink-800 text-white p-4 sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <button onClick={() => nav(-1)} className="p-2 -ml-2 rounded-lg active:bg-white/10">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="flex-1 min-w-0">
            <div className="text-xs opacity-70">Ordem de Produção</div>
            <div className="font-extrabold text-lg">#{op.op_numero}</div>
          </div>
          {op.maquina_codigo && (
            <div className="text-right text-xs">
              <div className="opacity-70">Máquina</div>
              <div className="font-bold">{op.maquina_codigo}</div>
            </div>
          )}
        </div>
      </header>

      <div className="p-4 space-y-3">
        {/* Cronômetro REGRESSIVO */}
        {etapaAtual?.iniciada_em && (
          <Cronometro etapa={etapaAtual} op={op} />
        )}

        {/* Card produto */}
        <div className="bg-white rounded-2xl p-4 shadow-sm">
          <div className="flex items-start justify-between mb-2">
            <div className="flex-1 min-w-0">
              <div className="font-bold text-base text-ink-900">{op.produto_descricao}</div>
              {op.produto_codigo && (
                <div className="text-xs font-mono text-ink-500 mt-0.5">{op.produto_codigo}</div>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-ink-600 mt-2">
            <span>Variante: <strong>{op.variante}</strong></span>
            {op.cliente_nome && <span>👤 {op.cliente_nome}</span>}
            {op.pedido_numero && <span>📋 Pedido #{op.pedido_numero}</span>}
            {op.fim_previsto && <span className={new Date(op.fim_previsto) < new Date() ? 'text-red-700 font-bold' : ''}>
              📅 até {new Date(op.fim_previsto).toLocaleDateString('pt-BR')}
            </span>}
          </div>
          <div className="grid grid-cols-2 gap-2 mt-3">
            <Indicator label="Qtd" valor={`${Number(op.quantidade_produzida).toLocaleString('pt-BR')}/${Number(op.quantidade).toLocaleString('pt-BR')}`} />
            <Indicator label="Peso (kg)" valor={`${Number(op.peso_produzido).toLocaleString('pt-BR')}/${Number(op.peso_planejado).toLocaleString('pt-BR')}`} />
            <Indicator label="Refugo" valor={Number(op.quantidade_refugo).toLocaleString('pt-BR')} cor="red" />
            <Indicator label="Custo MP" valor={`R$ ${op.custo_mp.toFixed(2)}`} cor="violet" />
          </div>
        </div>

        {/* Etapas */}
        <div className="bg-white rounded-2xl p-4 shadow-sm">
          <div className="text-xs uppercase text-ink-500 font-bold mb-2">Etapas</div>
          <div className="space-y-2">
            {op.etapas.map((e) => (
              <div key={e.id} className={`flex items-center gap-3 p-3 rounded-xl ${
                e.status === 'em_execucao' ? 'bg-emerald-50 border-2 border-emerald-400' :
                e.status === 'concluida' ? 'bg-ink-100 border border-ink-200 opacity-60' :
                'bg-ink-50 border border-ink-200'
              }`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                  e.status === 'em_execucao' ? 'bg-emerald-600 text-white' :
                  e.status === 'concluida' ? 'bg-emerald-200 text-emerald-700' :
                  'bg-ink-300 text-ink-600'
                }`}>{e.status === 'concluida' ? '✓' : e.ordem}</div>
                <div className="flex-1">
                  <div className="font-bold text-sm">{e.nome}</div>
                  <div className="text-xs text-ink-500">
                    {e.status === 'concluida' ? `${e.quantidade_ok} ok` :
                      e.pausada_em ? '⏸ pausada' :
                      e.status === 'em_execucao' ? '▶ em execução' : '⏳ pendente'}
                    {e.tempo_padrao_min > 0 && ` · ${e.tempo_padrao_min}min/un`}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Ficha (composição) — colapsável */}
        {op.composicao.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm">
            <button
              onClick={() => setShowFicha(s => !s)}
              className="w-full p-4 flex items-center justify-between"
            >
              <div className="flex items-center gap-2">
                <FlaskConical className="h-4 w-4 text-violet-600" />
                <span className="text-sm font-bold">Ficha técnica</span>
                {op.ficha_versao && (
                  <span className="text-[10px] bg-violet-100 text-violet-800 px-1.5 py-0.5 rounded font-mono">
                    v{op.ficha_versao}{op.ficha_revisao}
                  </span>
                )}
              </div>
              <span className="text-ink-400">{showFicha ? '▼' : '▶'}</span>
            </button>
            {showFicha && (
              <div className="px-4 pb-4 space-y-1.5">
                {op.composicao.map(c => (
                  <div key={c.materia_prima_id} className="flex items-center justify-between text-sm py-1.5 border-b border-ink-100 last:border-0">
                    <div>
                      <div className="font-medium">{c.nome}</div>
                      <div className="text-xs text-ink-500 font-mono">{c.codigo}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold">{c.qtd_total_op.toLocaleString('pt-BR')} {c.unidade}</div>
                      <div className="text-xs text-ink-500">R$ {c.custo_total.toFixed(2)}</div>
                    </div>
                  </div>
                ))}
                <div className="flex items-center justify-between pt-2 mt-2 border-t border-ink-200">
                  <div className="text-xs uppercase text-ink-500 font-bold">Custo total MP previsto</div>
                  <div className="font-extrabold text-violet-700">R$ {op.composicao.reduce((s, c) => s + c.custo_total, 0).toFixed(2)}</div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Lote */}
        {op.lote_codigo && (
          <div className="bg-white rounded-2xl p-3 shadow-sm flex items-center gap-3">
            <Package className="h-5 w-5 text-ink-500" />
            <div className="flex-1">
              <div className="text-xs uppercase text-ink-500 font-bold">Lote</div>
              <div className="font-mono text-sm">{op.lote_codigo}</div>
            </div>
            <span className={`text-xs px-2 py-0.5 rounded font-bold ${
              op.lote_status === 'disponivel' ? 'bg-emerald-100 text-emerald-800' :
              op.lote_status === 'aguardando_cura' ? 'bg-amber-100 text-amber-800' :
              op.lote_status === 'bloqueado' ? 'bg-red-100 text-red-800' :
              'bg-ink-100 text-ink-700'
            }`}>{op.lote_status}</span>
          </div>
        )}

        {/* Ações */}
        {etapaAtual && (
          <div className="bg-white rounded-2xl p-4 shadow-sm">
            <div className="text-xs uppercase text-ink-500 font-bold mb-3">
              Ação em: {etapaAtual.nome}
            </div>

            {etapaAtual.status === 'pendente' && (
              <button
                onClick={() => chamar('iniciar')}
                className="w-full h-16 bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white font-bold rounded-2xl flex items-center justify-center gap-2 text-lg"
              >
                <Play className="h-6 w-6" /> Iniciar etapa
              </button>
            )}

            {etapaAtual.status === 'em_execucao' && etapaAtual.pausada_em && (
              <button
                onClick={() => chamar('retorno')}
                className="w-full h-16 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-2xl flex items-center justify-center gap-2 text-lg"
              >
                <Play className="h-6 w-6" /> Retomar
              </button>
            )}

            {etapaAtual.status === 'em_execucao' && !etapaAtual.pausada_em && (
              <div className="grid grid-cols-2 gap-2">
                <ActionBtn icon={Plus} label="Apontar" cor="violet" onClick={() => setModal('parcial')} />
                <ActionBtn icon={Pause} label="Pausar" cor="amber" onClick={() => setModal('pausar')} />
                <ActionBtn icon={AlertTriangle} label="Refugo" cor="red" onClick={() => setModal('refugo')} />
                <ActionBtn icon={Square} label="Concluir" cor="emerald" onClick={() => setModal('fim')} />
              </div>
            )}
          </div>
        )}

        {!etapaAtual && (
          <div className="bg-emerald-50 rounded-2xl p-6 text-center">
            <CheckCircle2 className="h-12 w-12 text-emerald-600 mx-auto mb-2" />
            <div className="font-bold text-emerald-800">Todas etapas concluídas</div>
          </div>
        )}
      </div>

      {modal && etapaAtual && (
        <ModalApontar
          tipo={modal}
          token={token!}
          etapa_op_id={etapaAtual.id}
          operadorId={user?.id}
          onClose={() => setModal(null)}
          onDone={() => { setModal(null); load() }}
        />
      )}
    </div>
  )
}


function Cronometro({ etapa, op }: { etapa: Etapa; op: OPData }) {
  const [agora, setAgora] = useState(Date.now())
  useEffect(() => {
    const iv = setInterval(() => setAgora(Date.now()), 1000)
    return () => clearInterval(iv)
  }, [])

  if (!etapa.iniciada_em) return null

  const qtd = parseFloat(op.quantidade) || 0
  const peso = parseFloat(op.peso_planejado) || 0
  const tempoMin = op.tempo_producao_min || etapa.tempo_padrao_min || 0
  const capH = op.capacidade_hora_kg || 0

  // Tempo total em segundos
  let totalSeg = 0
  if (tempoMin > 0) totalSeg = tempoMin * qtd * 60
  else if (capH > 0 && peso > 0) totalSeg = (peso / capH) * 3600

  if (totalSeg <= 0) return null

  const inicio = new Date(etapa.iniciada_em).getTime()
  const decorrSegBruto = (agora - inicio) / 1000
  const tempoPausadoAtual = etapa.pausada_em ? (agora - new Date(etapa.pausada_em).getTime()) / 1000 : 0
  const decorrido = Math.max(0, decorrSegBruto - (etapa.tempo_pausado_seg || 0) - tempoPausadoAtual)
  const restante = totalSeg - decorrido
  const pct = Math.min(100, Math.max(0, (decorrido / totalSeg) * 100))

  const horas = Math.floor(Math.abs(restante) / 3600)
  const min = Math.floor((Math.abs(restante) % 3600) / 60)
  const seg = Math.floor(Math.abs(restante) % 60)
  const txt = `${horas.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}:${seg.toString().padStart(2, '0')}`
  const estourou = restante < 0
  const estaPausado = !!etapa.pausada_em

  const bgClass = estourou ? 'bg-red-50 border-red-400' :
                  estaPausado ? 'bg-amber-50 border-amber-400' :
                  'bg-emerald-50 border-emerald-400'
  const textClass = estourou ? 'text-red-700' :
                    estaPausado ? 'text-amber-700' :
                    'text-emerald-700'

  return (
    <div className={`rounded-2xl p-4 shadow-sm border-2 ${bgClass}`}>
      <div className={`text-[10px] uppercase font-bold text-center ${textClass}`}>
        {estourou ? '⚠ TEMPO ESTOURADO' : estaPausado ? '⏸ Pausado' : '⏱ Tempo restante'}
      </div>
      <div className={`text-5xl font-extrabold text-center font-mono mt-1 ${textClass}`}>
        {estourou && '−'}{txt}
      </div>
      <div className="mt-2 h-2 bg-ink-200 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all ${estourou ? 'bg-red-600' : 'bg-emerald-500'}`}
          style={{ width: `${Math.min(100, pct)}%` }} />
      </div>
      <div className="text-xs text-ink-500 mt-1 text-center">
        {Math.round(decorrido / 60)}min decorrido · meta {Math.round(totalSeg / 60)}min
      </div>
    </div>
  )
}


function Indicator({ label, valor, cor }: { label: string; valor: string; cor?: string }) {
  const cores: Record<string, string> = {
    red: 'bg-red-50 text-red-800',
    violet: 'bg-violet-50 text-violet-800',
  }
  return (
    <div className={`rounded-lg p-2 text-center ${cor ? cores[cor] : 'bg-ink-50'}`}>
      <div className="text-[10px] uppercase text-ink-500 font-medium">{label}</div>
      <div className="text-sm font-bold">{valor}</div>
    </div>
  )
}


function ActionBtn({ icon: Icon, label, cor, onClick }: { icon: any; label: string; cor: string; onClick: () => void }) {
  const cores: Record<string, string> = {
    violet: 'bg-violet-600 hover:bg-violet-700 active:bg-violet-800',
    amber: 'bg-amber-500 hover:bg-amber-600 active:bg-amber-700',
    red: 'bg-red-600 hover:bg-red-700 active:bg-red-800',
    emerald: 'bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800',
  }
  return (
    <button onClick={onClick} className={`h-20 ${cores[cor]} text-white font-bold rounded-2xl flex flex-col items-center justify-center gap-1 text-sm`}>
      <Icon className="h-6 w-6" />
      {label}
    </button>
  )
}


function ModalApontar({
  tipo, token, etapa_op_id, operadorId, onClose, onDone,
}: {
  tipo: 'parcial' | 'pausar' | 'refugo' | 'fim'
  token: string; etapa_op_id: number; operadorId?: number
  onClose: () => void; onDone: () => void
}) {
  const [qtd, setQtd] = useState('')
  const [peso, setPeso] = useState('')
  const [obs, setObs] = useState('')
  const [motivos, setMotivos] = useState<Motivo[]>([])
  const [motivoId, setMotivoId] = useState<number | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (tipo === 'pausar') apiPub.get<Motivo[]>(`/m/op/${token}/motivos-parada`).then(r => setMotivos(r.data))
    else if (tipo === 'refugo') apiPub.get<Motivo[]>(`/m/op/${token}/motivos-refugo`).then(r => setMotivos(r.data))
  }, [tipo, token])

  async function submit() {
    if ((tipo === 'pausar' || tipo === 'refugo') && !motivoId) {
      toast.error('Escolha o motivo'); return
    }
    if ((tipo === 'parcial' || tipo === 'fim' || tipo === 'refugo') && !qtd && !peso) {
      toast.error('Informe quantidade ou peso'); return
    }
    setLoading(true)
    try {
      const tipoApi = tipo === 'pausar' ? 'pausa' : tipo
      await apiPub.post(`/m/op/${token}/apontar`, {
        etapa_op_id, operador_id: operadorId, tipo: tipoApi,
        quantidade: parseFloat(qtd) || 0,
        peso_kg: parseFloat(peso) || 0,
        motivo_parada_id: tipo === 'pausar' ? motivoId : null,
        motivo_refugo_id: tipo === 'refugo' ? motivoId : null,
        observacao: obs || undefined,
      })
      toast.success('Registrado')
      onDone()
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Erro')
    } finally { setLoading(false) }
  }

  const titulos = { parcial: 'Apontar produção', pausar: 'Pausar etapa', refugo: 'Registrar refugo', fim: 'Concluir etapa' }
  const corBg = {
    parcial: 'bg-violet-600 hover:bg-violet-700',
    pausar: 'bg-amber-500 hover:bg-amber-600',
    refugo: 'bg-red-600 hover:bg-red-700',
    fim: 'bg-emerald-600 hover:bg-emerald-700',
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end justify-center">
      <div className="bg-white w-full rounded-t-3xl p-5 max-h-[95vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold">{titulos[tipo]}</h3>
          <button onClick={onClose} className="text-ink-500 text-3xl">×</button>
        </div>

        {(tipo === 'pausar' || tipo === 'refugo') && (
          <div className="mb-4">
            <div className="text-xs uppercase text-ink-500 font-bold mb-2">Motivo</div>
            <div className="grid grid-cols-2 gap-2">
              {motivos.map(m => (
                <button
                  key={m.id} onClick={() => setMotivoId(m.id)}
                  className={`p-3 rounded-xl text-sm font-medium border-2 text-left ${
                    motivoId === m.id ? 'border-violet-500 bg-violet-50' : 'border-ink-200'
                  }`}
                >
                  {m.nome}{m.reciclavel && <span className="ml-1">♻</span>}
                </button>
              ))}
            </div>
          </div>
        )}

        {(tipo === 'parcial' || tipo === 'fim' || tipo === 'refugo') && (
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div>
              <label className="text-xs uppercase text-ink-500 font-bold">Quantidade</label>
              <input type="number" inputMode="decimal" step="any"
                value={qtd} onChange={e => setQtd(e.target.value)} placeholder="0"
                className="mt-1 w-full h-14 text-2xl text-center border-2 border-ink-200 rounded-xl" />
            </div>
            <div>
              <label className="text-xs uppercase text-ink-500 font-bold">Peso (kg)</label>
              <input type="number" inputMode="decimal" step="any"
                value={peso} onChange={e => setPeso(e.target.value)} placeholder="0"
                className="mt-1 w-full h-14 text-2xl text-center border-2 border-ink-200 rounded-xl" />
            </div>
          </div>
        )}

        <input value={obs} onChange={e => setObs(e.target.value)} placeholder="Observação (opcional)"
          className="w-full h-12 px-4 border-2 border-ink-200 rounded-xl mb-4" />

        <button onClick={submit} disabled={loading}
          className={`w-full h-16 ${corBg[tipo]} text-white font-bold text-lg rounded-2xl flex items-center justify-center gap-2 disabled:opacity-40`}>
          {loading ? <Loader2 className="h-6 w-6 animate-spin" /> : 'Confirmar'}
        </button>
      </div>
    </div>
  )
}
